import { currencyChipHtml, escapeHtml } from '@colplat/ui-web';
import type { OutboxDocumentWrite } from '@colplat/core-client';
import { apiFetch } from '../api.js';
import { ageLabel, knownOffline, readCached } from '../offlineReads.js';
import { HISTORY_BATCH, type CachedHistory, type TransactionHistory } from './webledgerHistory.js';
import {
  heldRows, listRowsFor, queuedCreateRows, queuedUpdateRows,
  type AccountDto, type CurrencyDto, type TransactionDto,
} from './webledgerOfflineState.js';
import { recordState, RecordStateTracker } from './webledgerRecordState.js';
import { mountReviewBanner } from './webledgerReviewBanner.js';
import { mountRefusedBanner } from './webledgerRefusedBanner.js';
import {
  openTransactionEditor, reopenDiscardedEditor, transactionEditorModalHtml, wireSplitHelp,
} from './webledgerTransactionEditor.js';
import { bootstrapWebledgerClient, webledgerShellHtml, wireBackButton, type WebledgerClient } from '../webledgerClient.js';
import { wireHeadReveal } from './webledgerHeadReveal.js';

export async function mountWebledgerTransactions(root: HTMLElement): Promise<void> {
  root.innerHTML = shellHtml();
  wireBackButton(root);
  // The page's bar stays at the top as the small header; a tap on it brings
  // the whole header back (webledgerHeadReveal).
  wireHeadReveal(root.querySelector<HTMLElement>('#txBar')!);

  const clientOrNull = await bootstrapWebledgerClient(root);
  if (!clientOrNull) return;
  const client: WebledgerClient = clientOrNull;
  const { token } = client;

  const { docCache } = client;
  const [accountsRead, currenciesRead, transactionsRead] = await Promise.all([
    readCached<{ accounts: AccountDto[] }>(docCache, 'accounts', '/apps/webledger/accounts', token),
    readCached<{ currencies: CurrencyDto[] }>(docCache, 'currencies', '/apps/webledger/currencies', token),
    readCached<{ transactions: TransactionDto[] }>(docCache, 'transactions', '/apps/webledger/transactions', token),
  ]);
  if (!accountsRead.ok && accountsRead.status === 403) {
    root.querySelector('#wlBody')!.innerHTML = '<p class="text-secondary">You do not have access to this app.</p>';
    return;
  }
  if (!accountsRead.ok || !currenciesRead.ok || !transactionsRead.ok) {
    // Unreachable *and* nothing cached — a genuinely first visit while
    // offline. Say which it is instead of rendering an empty ledger, which
    // would read as "you have no transactions".
    root.querySelector('#wlBody')!.innerHTML =
      '<p class="text-secondary">This page has not been loaded on this device yet, and there is no connection. ' +
      'Open it once while online and it will keep working offline afterwards.</p>';
    return;
  }
  let accounts = accountsRead.data.accounts;
  let currencies = currenciesRead.data.currencies;
  let transactions = transactionsRead.data.transactions;
  /** Set while the visible data came from the cache rather than the server. */
  let staleSince: string | undefined = transactionsRead.stale ? transactionsRead.cachedAt : undefined;

  /**
   * Who is in each transaction — leases, the durable markers, subscriptions
   * and the TTL sweep, all in `webledgerRecordState.ts` so the reports page
   * uses the same one. See its module comment for why that matters.
   */
  const records = new RecordStateTracker(client, () => renderTransactions());

  let pendingCreates: TransactionDto[] = [];
  /**
   * Existing transactions with an edit queued offline, by record id. Unlike a
   * queued create these are not extra rows — the row already exists — so they
   * are a marker on it instead.
   */
  let pendingUpdates = new Map<string, TransactionDto>();
  /**
   * Drafts interrupted by somebody taking the record over. Not rows and not
   * queued work — the record already exists and these will never be sent on
   * their own — so, like `pendingUpdates`, a marker on the existing row.
   */
  let heldEdits = new Map<string, OutboxDocumentWrite>();
  records.seed(transactions);
  // Held offline edits: the shared banner, the same one the wallets page has.
  const reviewBanner = mountReviewBanner(root.querySelector<HTMLElement>('#wlReviewBanner')!, {
    token,
    online: () => client.transport.state === 'open',
    notify: client.notify,
    current: (id) => transactions.find((t) => t.id === id),
    onResolved: () => refreshTransactions(),
  });
  // Saved here and refused by the server. Opening one to change it happens
  // in the wallet log, which reads the unsent version; discarding works here.
  mountRefusedBanner(root.querySelector<HTMLElement>('#wlRefusedBanner')!, {
    entries: client.refusedEntries,
    onChange: client.onPendingWorkChange,
    discard: client.discardQueued,
  });

  const subscribeToListedRecords = (): void => records.subscribe(transactions.map((t) => t.id));

  client.transport.onStateChange((state) => {
    if (state !== 'open') {
      // Nothing re-reads while the socket is down, so without this the list
      // would sit there looking live — no locks updating, no new rows — with
      // only the chrome's indicator hinting why. Say it where the data is.
      staleSince = docCache.get('transactions')?.cachedAt ?? new Date().toISOString();
      renderOfflineBanner();
    }
    if (state === 'open') {
      // Nothing replays broadcasts missed while we were away, so re-seed the
      // whole lock picture from the server, then re-establish the subscription.
      records.resetSubscriptions();
      void refreshTransactions();
      subscribeToListedRecords();
      void flushOutbox();
      void primeHistory();
      return;
    }
    records.resetSubscriptions();
  });
  subscribeToListedRecords();
  // ...and flush for the same reason `subscribeToListedRecords` is called
  // here as well as from the state listener: the transport can reach 'open'
  // before this page attaches its listener. The flush itself is the client's
  // now (webledgerOutboxFlush.ts) and runs from every page; this list only
  // has to redraw when one has sent something.
  void flushOutbox();
  void primeHistory();
  client.onOutboxFlushed(() => {
    void refreshTransactions();
    void refreshPendingWrites();
    void refreshPendingRevisions();
  });

  client.transport.onMessage((msg) => {
    if (msg.t === 'recordEvent' && (msg.entity === 'account' || msg.entity === 'currency' || msg.entity === 'transaction')) {
      void refreshAll();
    }
    records.handleMessage(msg);
  });

  wireSplitHelp(root);

  /**
   * `onSaved` is declared to return `void`, so the async version is wrapped
   * rather than passed: a promise handed to a void-returning callback is a
   * rejection nothing is listening to, and a refresh that fails silently is
   * how a list keeps showing a transaction somebody just deleted.
   */
  const onSaved = (): void => { void afterSave(); };

  async function afterSave(): Promise<void> {
    await refreshTransactions();    // no-op while offline (falls back to cache)
    await refreshPendingWrites();   // ...but queued work must still show
  }

  wireNewTransactionButton(root, () => {
    void openTransactionEditor(root, client, null, accounts, currencies, onSaved);
  });

  renderTransactions();
  renderOfflineBanner();
  void refreshPendingWrites();
  void refreshPendingRevisions();
  // An editor this tab had open when the browser discarded it, back with its
  // unsaved values (webledgerFormRestore.ts).
  void reopenDiscardedEditor(root, client, accounts, currencies, onSaved);

  /**
   * Transactions created while offline, waiting in the outbox. They are shown
   * in the list straight away, marked, because the alternative is that a
   * transaction you just entered on a train simply is not there — which reads
   * as data loss whether or not it eventually syncs.
   *
   * Synthesised from the queued payload rather than fetched: by definition the
   * server has never seen them. `version: 0` and `pendingSync` keep them out
   * of every path that assumes a server-side record (they are not openable,
   * carry no lease, and no lock or owner state).
   */
  async function refreshPendingWrites(): Promise<void> {
    const queued = (await client.outbox.allDocuments()).filter((q) => q.entity === 'transaction');
    pendingCreates = queuedCreateRows(queued);
    pendingUpdates = queuedUpdateRows(queued);
    heldEdits = heldRows(queued);
    // Deletes hide their record in every view that reads the local copy, not
    // just this list — so the dataset is told, rather than this file
    // filtering its own rows and the wallet log still showing it.
    await client.refreshPendingWork();
    renderTransactions();
  }

  /**
   * Fill the history cache for **every** transaction, not just the ones
   * someone happened to open.
   *
   * "Available offline" has to mean the whole ledger, or the answer to "what
   * happened to this entry?" depends on whether you once clicked it — and at
   * a measured mean of 2.6 KB per transaction the lot is well under a
   * megabyte. One bulk request per 200 transactions rather than one per
   * transaction: 250 round trips for something that fits in two is a poor
   * trade.
   *
   * Version-aware, so this costs nothing in the steady state: a transaction's
   * history changes only when the transaction does, and the list already
   * tells us each one's `version`. Only the ones whose version has moved (or
   * that were never cached) are fetched.
   */
  async function primeHistory(): Promise<void> {
    if (client.transport.state !== 'open' || knownOffline()) return;

    const stale = transactions.filter((tx) => {
      const cached = docCache.get<CachedHistory>(`history:${tx.id}`);
      return cached?.value.atVersion !== tx.version;
    });

    for (let i = 0; i < stale.length; i += HISTORY_BATCH) {
      if (client.transport.state !== 'open') return;
      const batch = stale.slice(i, i + HISTORY_BATCH);
      const res = await apiFetch(
        `/apps/webledger/history?ids=${batch.map((t) => t.id).join(',')}`, { token },
      ).catch(() => undefined);
      if (!res?.ok) return;
      const { histories } = (await res.json()) as { histories: Record<string, TransactionHistory> };
      for (const tx of batch) {
        const history = histories[tx.id];
        if (history) await docCache.set(`history:${tx.id}`, { ...history, atVersion: tx.version });
      }
    }

    // No LRU: the cache is bounded by the ledger itself, and evicting entries
    // the prime just fetched would only make it fetch them again. What does
    // need dropping is history for transactions that no longer exist.
    const live = new Set(transactions.map((tx) => `history:${tx.id}`));
    for (const key of docCache.keysUnder('history:')) {
      if (!live.has(key)) await docCache.delete(key);
    }
  }

  /** The shared banner (webledgerReviewBanner.ts), mounted below. */
  async function refreshPendingRevisions(): Promise<void> {
    await reviewBanner.refresh();
  }

  /**
   * Send what is queued — the client's flush, shared by every page since
   * 2026-10-21 (webledgerOutboxFlush.ts). What this page adds is redrawing
   * its own list afterwards, which `onOutboxFlushed` above does.
   */
  async function flushOutbox(): Promise<void> {
    await client.flushOutbox();
    await refreshPendingWrites();
    await refreshPendingRevisions();
  }

  async function refreshAccounts(): Promise<void> {
    const read = await readCached<{ accounts: AccountDto[] }>(docCache, 'accounts', '/apps/webledger/accounts', token);
    if (read.ok && !read.stale) accounts = read.data.accounts;
  }
  async function refreshCurrencies(): Promise<void> {
    const read = await readCached<{ currencies: CurrencyDto[] }>(docCache, 'currencies', '/apps/webledger/currencies', token);
    if (read.ok && !read.stale) currencies = read.data.currencies;
  }
  async function refreshTransactions(): Promise<void> {
    const read = await readCached<{ transactions: TransactionDto[] }>(
      docCache, 'transactions', '/apps/webledger/transactions', token);
    if (!read.ok) return;
    // A *refresh* that falls back to the cache has nothing new to say — we
    // already have that data on screen. Only note that we are now stale.
    if (read.stale) { staleSince = read.cachedAt; renderOfflineBanner(); return; }
    staleSince = undefined;
    transactions = read.data.transactions;
    records.seed(transactions);
    subscribeToListedRecords();
    renderTransactions();
    renderOfflineBanner();
  }

  /**
   * Says, in the list itself, that what is on screen is a saved copy and how
   * old it is. The connection indicator in the page chrome says *that* we are
   * offline; this says what that means for the data in front of you — the two
   * are different questions and invariant #11's "state is never implied by
   * silence" applies to both.
   */
  function renderOfflineBanner(): void {
    const el = root.querySelector<HTMLElement>('#wlOfflineBanner');
    if (!el) return;
    if (!staleSince) { el.hidden = true; el.innerHTML = ''; return; }
    el.hidden = false;
    el.innerHTML =
      `<div class="alert alert-warning py-2 px-3 mb-2 small" role="status">` +
      `Offline — showing the copy saved on this device ${escapeHtml(ageLabel(staleSince))}. ` +
      `Locks and other people's changes are not live.</div>`;
  }
  async function refreshAll(): Promise<void> {
    await Promise.all([refreshAccounts(), refreshCurrencies(), refreshTransactions()]);
  }

  function renderTransactions(): void {
    const rows = listRowsFor(pendingCreates, transactions, pendingUpdates)
      // This list is built from a REST read, not the dataset, so the dataset's
      // own filter does not reach it.
      .filter((t) => !client.dataset.isPendingDelete('transaction', t.id));
    renderTransactionsTable(root, accounts, currencies, rows, records, pendingUpdates, heldEdits, (tx) => {
      if (tx.pendingSync && !pendingUpdates.has(tx.id)) {
        // A queued *create* has no server record yet, so there is nothing to
        // open — no lease, no version, no history.
        client.notify('This transaction has not synced yet, so it cannot be opened for editing.');
        return;
      }
      // A queued *update* does have a server record. Open the queued version,
      // not the server copy: that is what the user typed, and re-saving
      // replaces the outbox entry rather than adding a second one.
      const queued = pendingUpdates.get(tx.id);
      void openTransactionEditor(root, client, queued ?? tx, accounts, currencies, onSaved);
    });
  }
}

function shellHtml(): string {
  return webledgerShellHtml('transactions', `
    <div class="cf-card cf-card--bleed p-3">
      <div class="d-flex justify-content-between align-items-center mb-2" id="txBar">
        <div class="cf-h">Transactions</div>
        <button class="btn btn-sm btn-primary" type="button" id="newTxBtn">New transaction</button>
      </div>
      <div id="wlOfflineBanner" hidden></div>
      <div id="wlRefusedBanner" hidden></div>
      <div id="wlReviewBanner" hidden></div>
      <div id="transactionsTable"></div>
    </div>`) +
    transactionEditorModalHtml() +
    `<div id="wlTimeline"></div>` +
    `<div class="cf-sr" aria-live="polite" aria-atomic="true"></div>`;
}

export function renderTransactionsTable(
  root: HTMLElement, accounts: AccountDto[], currencies: CurrencyDto[], transactions: TransactionDto[],
  records: RecordStateTracker,
  pendingUpdates: Map<string, TransactionDto>, heldEdits: Map<string, OutboxDocumentWrite>,
  onOpen: (tx: TransactionDto) => void,
): void {
  const accountName = (id: string): string => accounts.find((a) => a.id === id)?.name ?? id.slice(0, 8);
  const currenciesByCode = new Map(currencies.map((c) => [c.code, c]));
  const container = root.querySelector<HTMLElement>('#transactionsTable')!;
  if (transactions.length === 0) {
    container.innerHTML = '<p class="text-secondary mb-0">No transactions yet.</p>';
    return;
  }
  const rowsHtml = transactions.map((tx) => {
    // Every leg is in the transaction's own currency (the manifest's `value`
    // field), so the chips all carry the same code — which is the point:
    // legacy chips every signed amount in the app, and a register that mixes
    // HUF and EUR rows is exactly where an unlabelled 469 is worth misreading.
    // Decimals come from that currency's `fraction`, not the stored 4dp.
    const txCur = currenciesByCode.get(tx.currencyCode);
    const legs = tx.splits
      .map((s) => `<span class="wl-leg">${escapeHtml(accountName(s.accountId))}`
        + `${currencyChipHtml(s.value, tx.currencyCode, txCur ? { currency: txCur } : {})}</span>`)
      .join('');

    const holder = records.lockedBy.get(tx.id);
    // Two distinct states, not one: someone typing right now, versus someone's
    // unfinished edit whose lease has lapsed (their laptop slept, their train
    // went into a tunnel). The second is the one Take over exists for.
    const owner = holder ? undefined : records.ownedBy.get(tx.id);
    const state = recordState({
      tx, holder, owner, held: heldEdits.get(tx.id), queuedEdit: pendingUpdates.has(tx.id),
    });

    // A transaction created on this device and never sent has no server
    // record to open — no lease, no version, no history — so its description
    // is text rather than a button. An existing record with a queued edit is
    // still openable.
    const openable = !tx.pendingSync || pendingUpdates.has(tx.id);
    const title = openable
      ? `<button type="button" data-open-tx="${tx.id}">${escapeHtml(tx.description || '(no description)')}</button>`
      : `<span class="text-secondary">${escapeHtml(tx.description || '(no description)')}</span>`;

    /*
     * The whole row opens, not just its description.
     *
     * The description has always been a button and the row around it was
     * inert, which on a phone means the only way in is a target a few
     * characters wide — reported as "tapping the title opens it, tapping
     * anywhere else just highlights the row". The button stays: it is the
     * keyboard and screen-reader path, and it is what says the row is an
     * action rather than a place. The row simply stops swallowing the taps
     * that miss it.
     */
    return `<tr class="${state.cls}"${openable ? ` data-open-row="${escapeHtml(tx.id)}"` : ''}${state.color ? ` style="--row-color:${escapeHtml(state.color)}"` : ''}>
      <td class="cf-gut"><span></span></td>
      <td class="cf-date">${escapeHtml(tx.txDate)}</td>
      <td class="cf-title">${state.chip}${title}
        <div class="wl-tx-sub">${escapeHtml(tx.currencyCode)} · ${legs}</div></td>
      <td class="wl-tx-cur">${escapeHtml(tx.currencyCode)}</td>
      <td class="text-secondary wl-tx-legs">${legs}</td>
    </tr>`;
  }).join('');
  container.innerHTML = `<div class="cf-table-scroll"><table class="table cf-table align-middle wl-tx-list">
    <thead><tr><th class="cf-gut"></th><th>Date</th><th>Description</th>
      <th class="wl-tx-cur">Currency</th><th class="wl-tx-legs">Splits</th></tr></thead>
    <tbody>${rowsHtml}</tbody></table></div>`;
  /*
   * One delegated handler for the row, which the description button's own
   * click bubbles into — so there is a single way in and no chance of both
   * firing and opening twice.
   *
   * A row that is not openable carries no `data-open-row` and therefore does
   * nothing, which is the same answer its description already gives by being
   * text rather than a button.
   */
  container.addEventListener('click', (event) => {
    const row = (event.target as HTMLElement).closest<HTMLElement>('[data-open-row]');
    if (!row) return;
    const tx = transactions.find((t) => t.id === row.dataset['openRow']);
    if (tx) onOpen(tx);
  });
  container.querySelectorAll<HTMLButtonElement>('[data-open-tx]').forEach((btn) => {
    btn.addEventListener('click', (event) => {
      // Handled by the row above; without this the same tap opens twice.
      event.stopPropagation();
      const tx = transactions.find((t) => t.id === btn.dataset['openTx']);
      if (tx) onOpen(tx);
    });
  });
}

function wireNewTransactionButton(root: HTMLElement, onClick: () => void): void {
  root.querySelector('#newTxBtn')?.addEventListener('click', onClick);
}

